import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import { ModelSelect } from './ModelSelect';
import { PreviewModal } from './PreviewModal';
import { SearchModal } from './SearchModal';
import { ChatSearchBar } from './ChatSearchBar';

import { ChatInput } from './ChatInput';
import { SplashScreen } from './SplashScreen';
import { useDocumentStore } from '../stores/documentStore';
import { useDocumentSelectionStore } from '../stores/documentSelectionStore';
import { useChatHistoryStore } from '../stores/chatHistoryStore';
import { useChatListStore } from '../stores/chatListStore';
import { useChatUIStore } from '../stores/chatUIStore';
import { MessageList } from './MessageList';
import { Toast } from './common/Toast';
import { usePlatformStore } from '../stores/platformStore';
import { useToastStore } from '../stores/ToastStore';
import { useTTSStore } from '../stores/ttsStore';
import { MessageModel } from '../types/models';
import { useChatSearch } from '../hooks/useChatSearch';
import { apiClient, processChatCompletionStream } from '../clients/APIClient';
import { scanFiles } from '../utils/fileScanner';
import { useMagazineImport } from '../hooks/useMagazineImport';
import DragAndDropModal, { DragAndDropResult } from './DragAndDropModal';
import { ChatRenameModal } from './chat-management/ChatRenameModal';
import { useUserSettings } from '../stores/settingsRegistry';
import { useGenerationCompleteNotifier } from '../hooks/useGenerationCompleteNotifier';

// TODO: Maybe needs a document clear function so that documents are automatically cleared when the chat is sent. Really depends
// 		 On how doc / rag maintains the instructions between messages.
interface ChatProps {
	chatInputRef: React.RefObject<any>;
}

export function Chat({ chatInputRef }: ChatProps) {
	const chatId = useChatHistoryStore((s) => s.chatId);
	const chatHistory = useChatHistoryStore((s) => s.chatHistory);
	const inputs = useChatHistoryStore((s) => s.inputs);
	const isGenerating = useChatHistoryStore((s) => s.isGenerating);
	const setIsGenerating = useChatHistoryStore((s) => s.setIsGenerating);
	const setHistory = useChatHistoryStore((s) => s.setHistory);
	const setInputs = useChatHistoryStore((s) => s.setInputs);
	const selectedModel = useChatHistoryStore((s) => s.selectedModel);
	const setSelectedModel = useChatHistoryStore((s) => s.setSelectedModel);

	const refreshChats = useChatListStore((s) => s.refreshChats);
	const onNewChat = useChatListStore((s) => s.onNewChat);
	const onModelChange = useChatListStore((s) => s.onModelChange);
	const stopGeneration = useChatListStore((s) => s.stopGeneration);
	const renameChat = useChatListStore((s) => s.renameChat);
	const stopTTS = useTTSStore((s) => s.stop);

	const isSplashVisible = useChatUIStore((s) => s.isSplashVisible);
	const renameOpen = useChatUIStore((s) => s.renameOpen);
	const targetChatId = useChatUIStore((s) => s.targetChatId);
	const targetChatTitle = useChatUIStore((s) => s.targetChatTitle);
	const setRenameOpen = useChatUIStore((s) => s.setRenameOpen);
	const setTargetChatId = useChatUIStore((s) => s.setTargetChatId);
	const setTargetChatTitle = useChatUIStore((s) => s.setTargetChatTitle);

	const selectedFilesDoc = useDocumentSelectionStore(
		(s) => s.selectedFilesDoc,
	);
	const fetchDocuments = useDocumentStore((s) => s.fetchDocuments);
	const { importMagazines, isImporting: isMagazineImporting } = useMagazineImport({ placement: 'chat' });

	const { toast: contextToast, showToast, isModalOpen } = useToastStore();

	const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
	const [attachedImages, setAttachedImages] = useState<string[]>([]);
	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const [pendingFolders, setPendingFolders] = useState<string[]>([]);
	const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
	const [isUploadingDocument, setIsUploadingDocument] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [_dragCounter, setDragCounter] = useState(0);

	const autoScrollRef = useRef(true);
	const reEnableTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const scrollRafRef = useRef<number | null>(null);
	const lastScrollTopRef = useRef(0);
	const previousInputsLengthRef = useRef(0);
	const previousIsGeneratingRef = useRef(isGenerating);

	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isChatSearchOpen, setIsChatSearchOpen] = useState(false);

	const chatContainerRef = useRef<HTMLDivElement>(null);
	const dropZoneRef = useRef<HTMLDivElement>(null);

	const updateAutoScroll = useCallback((enabled: boolean) => {
		autoScrollRef.current = enabled;
	}, []);

	const queueScrollToBottom = useCallback(() => {
		if (scrollRafRef.current !== null) {
			cancelAnimationFrame(scrollRafRef.current);
		}

		scrollRafRef.current = requestAnimationFrame(() => {
			scrollRafRef.current = null;
			if (!chatContainerRef.current || !autoScrollRef.current) return;
			// Moves the container to the bottom
			chatContainerRef.current.scrollTop =
				chatContainerRef.current.scrollHeight;
		});
	}, []);

	const { helpers: settingsHelpers } = useUserSettings();
	const modelSupportsVLM = settingsHelpers.isVLM(selectedModel);

	useGenerationCompleteNotifier();

	const {
		handleSearch,
		handleRoleFilterChange,
		navigateToMatch,
		clearSearch,
		totalMatches,
		currentMatchIndex,
		roleFilter,
		getHighlightData,
	} = useChatSearch(chatHistory, chatContainerRef, isGenerating);

	// Refocus chat input when search modal closes
	useEffect(() => {
		if (!isSearchOpen) {
			requestAnimationFrame(() => {
				chatInputRef.current?.textareaRef?.current?.focus();
			});
		}
	}, [isSearchOpen, chatInputRef]);

	// Keyboard shortcut for in-chat search (Ctrl+F)
	const isDesktop = usePlatformStore((state) => state.isDesktop());

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Ctrl+F or Cmd+F to open search - only in desktop/electron mode
			if (isDesktop && (e.ctrlKey || e.metaKey) && e.key === 'f') {
				e.preventDefault();
				setIsChatSearchOpen(true);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isDesktop]);

	// Clear search when closing search bar
	useEffect(() => {
		if (!isChatSearchOpen) {
			clearSearch();
		}
	}, [isChatSearchOpen, clearSearch]);

	const platform = usePlatformStore((state) => state.platform);

	// Platform-specific margin top for model select container
	const platformMarginTop =
		platform === 'darwin'
			? 'mt-[24px]'
			: platform === 'win32'
				? 'mt-[48px]'
				: 'mt-[48px]';

	// reset scroll to bottom whenever the user switches to a new chat
	useEffect(() => {
		if (chatContainerRef?.current) {
			chatContainerRef.current.scrollTop =
				chatContainerRef.current.scrollHeight;
			updateAutoScroll(true);
		}
	}, [chatId, updateAutoScroll]);

	// When user sends a new message, enable auto-scroll and scroll to bottom
	useEffect(() => {
		// check if chatInput has new input
		if (inputs.length > previousInputsLengthRef.current) {
			updateAutoScroll(true);
			if (chatContainerRef?.current) {
				queueScrollToBottom();
			}
		}
		previousInputsLengthRef.current = inputs.length;
	}, [inputs, queueScrollToBottom, updateAutoScroll]);

	// Reset auto-scroll state when generation starts or stops
	useEffect(() => {
		if (isGenerating) {
			if (reEnableTimeoutRef.current) {
				clearTimeout(reEnableTimeoutRef.current);
				reEnableTimeoutRef.current = null;
			}

			updateAutoScroll(true);
			queueScrollToBottom();
		}
	}, [isGenerating, updateAutoScroll, queueScrollToBottom]);

	// Auto-scroll to bottom when content changes during generation
	useEffect(() => {
		if (!chatContainerRef?.current || !isGenerating || !autoScrollRef.current)
			return;

		queueScrollToBottom();
	}, [chatHistory, isGenerating, queueScrollToBottom]);

	// Handle user scroll events
	// eslint-disable-next-line consistent-return
	useEffect(() => {
		const chatContainer = chatContainerRef?.current;
		if (!chatContainer) return;
		lastScrollTopRef.current = chatContainer.scrollTop;

		const disableAutoScroll = () => {
			if (reEnableTimeoutRef.current) {
				clearTimeout(reEnableTimeoutRef.current);
				reEnableTimeoutRef.current = null;
			}
			if (scrollRafRef.current !== null) {
				cancelAnimationFrame(scrollRafRef.current);
				scrollRafRef.current = null;
			}
			updateAutoScroll(false);
		};

		const handleScroll = () => {
			if (!chatContainer) return;
			const currentScrollTop = chatContainer.scrollTop;
			const scrolledUp = currentScrollTop < lastScrollTopRef.current;

			const isAtBottom =
				chatContainer.scrollHeight - chatContainer.scrollTop <=
				chatContainer.clientHeight + 20;

			if (isAtBottom) {
				updateAutoScroll(true);
			} else if (scrolledUp) {
				disableAutoScroll();
			}

			lastScrollTopRef.current = currentScrollTop;
		};

		const handleWheel = (event: WheelEvent) => {
			// User intent to read earlier content: unlock immediately.
			if (isGenerating && event.deltaY < 0 && autoScrollRef.current) {
				disableAutoScroll();
			}
		};

		chatContainer.addEventListener('scroll', handleScroll, {
			passive: true,
		});
		// Attach a mouse-wheel/ trackpad listener to the chat scroll
		chatContainer.addEventListener('wheel', handleWheel, {
			passive: true,
		});

		return () => {
			chatContainer.removeEventListener('scroll', handleScroll);
			chatContainer.removeEventListener('wheel', handleWheel);
			if (reEnableTimeoutRef.current) {
				clearTimeout(reEnableTimeoutRef.current);
				reEnableTimeoutRef.current = null;
			}
			// Cancel any pending scroll-to-bottom RAFs
			if (scrollRafRef.current !== null) {
				cancelAnimationFrame(scrollRafRef.current);
				scrollRafRef.current = null;
			}
		};
	}, [isGenerating, updateAutoScroll]);

	useEffect(() => {
		// --- Streaming chunk buffer for rAF-throttled state updates ---
		// Instead of calling setHistory on every SSE chunk (which triggers a full
		// React reconciliation per token), we buffer incoming content and flush
		// at most once per animation frame (~60fps).
		let contentBuffer = '';
		let pendingCitations: any[] | null = null;
		let pendingToolCalls: any[] | null = null;
		let pendingDone = false;
		let rafId: number | null = null;

		const flushBuffer = () => {
			rafId = null;
			const bufferedContent = contentBuffer;
			const bufferedCitations = pendingCitations;
			const bufferedToolCalls = pendingToolCalls;
			const isDone = pendingDone;

			// Reset buffer
			contentBuffer = '';
			pendingCitations = null;
			pendingToolCalls = null;
			pendingDone = false;

			setHistory((prevMessages) => {
				const newMessages = [...prevMessages];
				let assistantMessageIndex = newMessages.length - 1;

				const ensureAssistantMessage = () => {
					const currentLast = newMessages[assistantMessageIndex];
					if (currentLast && currentLast.role === 'assistant') {
						return currentLast;
					}

					newMessages.push({
						role: 'assistant',
						content: '',
						model: selectedModel,
					});
					assistantMessageIndex = newMessages.length - 1;
					return newMessages[assistantMessageIndex];
				};

				let updatedLast: (typeof newMessages)[number] | null = null;

				if (bufferedContent) {
					const assistantMessage = updatedLast ?? ensureAssistantMessage();
					updatedLast = {
						...assistantMessage,
						content: (assistantMessage.content || '') + bufferedContent,
						model: assistantMessage.model || selectedModel,
					};
				}

				if (bufferedCitations && bufferedCitations.length > 0) {
					const assistantMessage = updatedLast ?? ensureAssistantMessage();
					const localPaths = useDocumentStore.getState().localFilePaths;
					const enrichedCitations = bufferedCitations.map((c: any) => {
						const fileId = c.source?.file_id;
						if (fileId && localPaths[fileId] && !c.source?.local_path) {
							return { ...c, source: { ...c.source, local_path: localPaths[fileId] } };
						}
						return c;
					});
					updatedLast = {
						...assistantMessage,
						citations: enrichedCitations,
						model: assistantMessage.model || selectedModel,
					};
				}

				if (bufferedToolCalls && bufferedToolCalls.length > 0) {
					const assistantMessage = updatedLast ?? ensureAssistantMessage();
					updatedLast = {
						...assistantMessage,
						tool_calls: bufferedToolCalls,
						model: assistantMessage.model || selectedModel,
					};
				}

				if (updatedLast) {
					newMessages[assistantMessageIndex] = updatedLast;
				}

				if (isDone) {
					const finalMessage =
						updatedLast ?? newMessages[assistantMessageIndex];
					if (finalMessage && finalMessage?.role === 'assistant') {
						setInputs((prevInputs) => {
							const lastInput = prevInputs[prevInputs.length - 1];
							if (lastInput && lastInput.role === 'assistant') {
								return prevInputs;
							}
							return [...prevInputs, finalMessage];
						});
					}
				}

				if (autoScrollRef.current) {
					queueScrollToBottom();
				}

				return newMessages;
			});
		};

		const onStreamChunk = (event: CustomEvent) => {
			const data = event.detail as {
				content?: string;
				done?: boolean;
				toolCalls: any[];
				citations?: any[];
			};

			// Accumulate content into buffer
			if (data.content) {
				contentBuffer += data.content;
			}
			if (data.citations && data.citations.length > 0) {
				pendingCitations = data.citations;
			}
			if (data.toolCalls && data.toolCalls.length > 0) {
				pendingToolCalls = data.toolCalls;
			}
			if (data.done) {
				pendingDone = true;
				// Flush immediately on done to ensure final state is committed
				if (rafId !== null) {
					cancelAnimationFrame(rafId);
				}
				flushBuffer();
				return;
			}

			// Schedule a flush on the next animation frame if not already pending
			if (rafId === null) {
				rafId = requestAnimationFrame(flushBuffer);
			}
		};

		const onRefresh = () => refreshChats();

		// Listen to stream chunks and completion events
		window.addEventListener(
			'chat-completion-stream-chunk',
			onStreamChunk as EventListener,
		);
		window.addEventListener(
			'chat-completion-finished',
			onRefresh as EventListener,
		);

		return () => {
			// Cancel any pending rAF flush
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}
			window.removeEventListener(
				'chat-completion-stream-chunk',
				onStreamChunk as EventListener,
			);
			window.removeEventListener(
				'chat-completion-finished',
				onRefresh as EventListener,
			);
		};
	}, [setHistory, setInputs, refreshChats, selectedModel, queueScrollToBottom]);

	const handleChatHistoryUpdate = useCallback(() => {
		if (!chatId) return;
		// Only persist if there are actual messages in the chat
		if (chatHistory.length === 0) return;

		// Always persist current history for the active chat to update last_updated
		apiClient.history
			.updateHistoryById(chatId, chatHistory)
			.then(() => {
				return refreshChats();
			})
			.catch((err) => {
				console.error(err);
			});
	}, [chatId, chatHistory, refreshChats]);

	// Handles the callback from the ModelSelect component to update the selected model
	const handleModelChange = (model: string) => {
		// Add generation stop here so generation stops when the model is changed
		if (isGenerating) {
			stopGeneration();
			handleChatHistoryUpdate();
		}

		setSelectedModel(model);
		onModelChange();
		console.log(model);
	};
	const handleRemoveFile = (file: string) => {
		setSelectedFiles((prevFiles) => prevFiles.filter((f) => f !== file));
		setAttachedImages((prevImages) => prevImages.filter((f) => f !== file));
	};

	const handleFeedback = useCallback((index: number, value: -1 | 1) => {
		const history = useChatHistoryStore.getState().chatHistory;
		const currentChatId = useChatHistoryStore.getState().chatId;
		if (!currentChatId || !history[index]) return;

		const current = history[index].feedback ?? 0;
		const next: -1 | 0 | 1 = current === value ? 0 : value; // toggle to 0 if same

		const updated = history.map((msg, i) =>
			i === index ? { ...msg, feedback: next } : msg,
		);

		setHistory(updated);

		apiClient.history
			.updateHistoryById(currentChatId, updated)
			.catch((err) => console.error('Failed to persist feedback', err));

		switch (next) {
			case -1:
				showToast('Feedback Recorded', 'error', {
					timeout: 3000,
					placement: 'chat',
				});
				break;
			case 1:
				showToast('Feedback Recorded', 'success', {
					timeout: 3000,
					placement: 'chat',
				});
				break;
			default:
				// 0 / error
				showToast('Feedback Removed', 'info', {
					timeout: 3000,
					placement: 'chat',
				});
				break;
		}
	}, [setHistory, showToast]);

	// Drag and drop files handlers
	const handleDragEnter = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// Check if files are being dragged (not text)
		const hasFiles = e.dataTransfer.types.includes('Files');
		// Check if files are being dragged
		if (
			e.dataTransfer.items &&
			e.dataTransfer.items.length > 0 &&
			hasFiles
		) {
			setDragCounter((prev) => {
				const newCounter = prev + 1;
				return newCounter;
			});
			setIsDragging((prev) => {
				if (!prev) {
					return true;
				}
				return prev;
			});
		}
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// Check if we're leaving the drop window(currently entire Chat window, not including sidebar) entirely
		const rect = dropZoneRef.current?.getBoundingClientRect();
		if (rect) {
			const isLeavingDropZone =
				e.clientX <= rect.left ||
				e.clientX >= rect.right ||
				e.clientY <= rect.top ||
				e.clientY >= rect.bottom;

			if (isLeavingDropZone) {
				setIsDragging(false);
				setDragCounter(0);
				return;
			}
		}

		setDragCounter((prev) => {
			const newCounter = prev - 1;
			if (newCounter === 0) {
				setIsDragging(false);
			}
			return newCounter;
		});
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// Set the drop effect
		if (e.dataTransfer) {
			e.dataTransfer.dropEffect = 'copy';
		}
	}, []);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();

			setIsDragging(false);
			setDragCounter(0);

			if (isUploadingDocument || isGenerating || isMagazineImporting) {
				showToast(
					'Please wait for the current operation to complete',
					'warning',
					{ timeout: 3000, placement: 'chat', adaptToModal: true },
				);
				return;
			}

			if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
				try {
					const { files, topLevelFolders } = await scanFiles(
						e.dataTransfer.items,
					);
					if (files.length > 0) {
						const isOnlyImages = files.every((f) =>
							f.type.startsWith('image/'),
						);
						if (isOnlyImages && chatInputRef.current) {
							// If ONLY images, attach them directly to the chat input
							chatInputRef.current.processDroppedFiles(files);
						} else {
							// open file upload modal
							setPendingFiles(files);
							setPendingFolders(topLevelFolders);
							setShowUploadConfirmation(true);
						}
					}
				} catch (error) {
					console.error('Error scanning files:', error);
					showToast('Failed to process dropped files', 'error', {
						timeout: 3000,
						placement: 'chat',
					});
				}
			}
		},
		[isUploadingDocument, chatInputRef,isGenerating, isMagazineImporting, showToast],
	);

	const handleConfirmUpload = async (result: DragAndDropResult) => {
		setShowUploadConfirmation(false);

		if (result.isMagazineImport) {
			await importMagazines(result.files);
		} else {
			if (isUploadingDocument || isGenerating || isMagazineImporting) {
				showToast(
					'Please wait for the current operation to complete',
					'warning',
					{ timeout: 3000, placement: 'chat', adaptToModal: true },
				);
				setPendingFiles([]);
				setPendingFolders([]);
				return;
			}

			const filesToUpload = result.files || [];
			if (chatInputRef.current) {
				chatInputRef.current.processDroppedFiles(filesToUpload);
			}
		}

		setPendingFiles([]);
		setPendingFolders([]);
	};

	const handleCancelUpload = () => {
		setShowUploadConfirmation(false);
		setPendingFiles([]);
		setPendingFolders([]);
	};

	// Persist chat history only when generation completes (true -> false),
	// and only if the latest message is an assistant response.
	useEffect(() => {
		const wasGenerating = previousIsGeneratingRef.current;
		previousIsGeneratingRef.current = isGenerating;

		if (!(wasGenerating && !isGenerating)) return;

		const lastMessage = chatHistory[chatHistory.length - 1];
		if (lastMessage?.role !== 'assistant') return;

		handleChatHistoryUpdate();
	}, [isGenerating, chatHistory, handleChatHistoryUpdate]);

	// Add effect to handle dragging outside the window
	useEffect(() => {
		const handleWindowDragLeave = (e: DragEvent) => {
			// Check if the drag is leaving the window entirely
			if (e.clientX === 0 && e.clientY === 0) {
				setIsDragging(false);
				setDragCounter(0);
			}
		};

		const handleWindowDrop = (e: DragEvent) => {
			// Prevent default drop behavior on window
			e.preventDefault();
			// Reset drag state if drop happens outside our drop zone
			if (!dropZoneRef.current?.contains(e.target as Node)) {
				console.log('[Window Drop] Drop outside chat window');
				setIsDragging(false);
				setDragCounter(0);
			}
		};

		const handleWindowDragOver = (e: DragEvent) => {
			// Prevent default to allow drop
			e.preventDefault();
		};

		// Add window-level listeners
		window.addEventListener('dragleave', handleWindowDragLeave);
		window.addEventListener('drop', handleWindowDrop);
		window.addEventListener('dragover', handleWindowDragOver);

		return () => {
			window.removeEventListener('dragleave', handleWindowDragLeave);
			window.removeEventListener('drop', handleWindowDrop);
			window.removeEventListener('dragover', handleWindowDragOver);
		};
	}, []);

	const handleEditMessage = useCallback(
		async (messageIndex: number, newContent: string) => {
			const currentSelectedDocuments =
				useDocumentSelectionStore.getState().selectedFilesDoc;

			// Stop any ongoing generation or TTS
			if (isGenerating) {
				await stopGeneration();
			} else {
				stopTTS();
			}

			// Find where to slice the history (remove this message and all after it)
			const newHistory = chatHistory.slice(0, messageIndex);
			const newInputs = inputs.slice(0, messageIndex);

			// Update the state with truncated history
			setHistory(newHistory);
			setInputs(newInputs);

			const editedMessage: MessageModel = {
				role: 'user',
				content: newContent,
				images: chatHistory[messageIndex]?.images,
				documents: currentSelectedDocuments,
			};

			setHistory((prevMessages: MessageModel[]) => [
				...prevMessages,
				editedMessage,
			]);

			const backendMessage: MessageModel = {
				role: 'user',
				content: newContent,
				images: chatHistory[messageIndex]?.images,
				documents: currentSelectedDocuments,
			};

			// Add to inputs for backend processing
			setInputs((prevMessages: MessageModel[]) => [
				...prevMessages,
				backendMessage,
			]);

			setIsGenerating(true);

			try {
				const response = await apiClient.chat.chatCompletionStream(
					[...newInputs, backendMessage],
					selectedModel,
					currentSelectedDocuments,
					true,
				);
				await processChatCompletionStream(response);
			} catch (err) {
				console.error('Error during regeneration after edit:', err);
				showToast('Failed to regenerate response', 'error', {
					timeout: 5000,
					placement: 'chat',
				});
			} finally {
				setIsGenerating(false);
				window.dispatchEvent(new CustomEvent('chat-completion-finished'));
			}
		},
		[
			chatHistory,
			inputs,
			isGenerating,
			stopGeneration,
			stopTTS,
			setHistory,
			setInputs,
			setIsGenerating,
			selectedModel,
			selectedFilesDoc,
			showToast,
		],
	);

	if (isSplashVisible) {
		return <SplashScreen />;
	}

	return (
		<>
			<div
				ref={dropZoneRef}
				data-chat-root
				className="relative flex flex-col justify-between w-full min-w-[400px] h-screen overflow-hidden"
				onDragEnter={handleDragEnter}
			>
				{isDragging && (
					<div
						className="fixed inset-0 z-50 pointer-events-none"
						onDragEnter={handleDragEnter}
						onDragLeave={handleDragLeave}
						onDragOver={handleDragOver}
						onDrop={handleDrop}
						style={{ pointerEvents: isDragging ? 'auto' : 'none' }}
					>
						<div className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm">
							<div className="h-full flex items-center justify-center pl-64">
								<div className="bg-neutral-600 border-2 border-dashed border-red-500 rounded-xl p-12 mx-8 shadow-2xl animate-pulse">
									<div className="text-center">
										<i className="fas fa-cloud-upload-alt text-6xl text-red-500 mb-4" />
										<h3 className="text-2xl font-bold text-neutral-100 mb-2">
											Drop files here
										</h3>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				<div
					className={`${platformMarginTop} top-0 w-full mx-auto px-lg mb-md`}
					style={{ WebkitAppRegion: 'drag' } as any}
				>
					<div className="flex items-center gap-3 w-full">
						{/* Left section: Search button + Model Select */}
						<div className="flex items-center gap-3 flex-shrink-0">
							{/* Search Button - Self-contained, easy to move */}
							<button
								className="w-10 h-10 flex items-center justify-center bg-neutral-500 border-0 cursor-pointer rounded-pill transition-colors duration-200 hover:bg-red-600"
								style={{ WebkitAppRegion: 'no-drag' } as any}
								aria-label='Search chats and documents'
								title="Search chats and documents"
								onClick={() => setIsSearchOpen(true)}
							>
								<i className="fas fa-search text-neutral-100 text-lg" />
							</button>

							<ModelSelect
								onModelChange={handleModelChange}
								isModelVLMSupported={modelSupportsVLM}
							/>
						</div>

						{/* Spacer */}
						<div className="flex-1" />

						{/* Right section: New Chat button */}
						<div className="flex-shrink-0">
							<button
								data-testid="new-chat-btn"
								className="w-10 h-10 flex bg-neutral-500 border-0 cursor-pointer rounded-pill px-2.5 transition-colors duration-200 hover:bg-red-600"
								style={{ WebkitAppRegion: 'no-drag' } as any}
								title="New chat"
								aria-label='Start a new chat'
								onClick={async () => {
									if (isGenerating) {
										await stopGeneration();
										handleChatHistoryUpdate();
									}
									onNewChat();
									setIsChatSearchOpen(false);
									// Refocus chat input after creating new chat
									requestAnimationFrame(() => {
										chatInputRef.current?.textareaRef?.current?.focus();
									});
								}}
							>
								<i className="fas fa-pen-to-square text-neutral-100 text-lg self-center mx-auto" />
							</button>
						</div>

						{/* SearchModal - follows the button */}
						{isSearchOpen && (
							<SearchModal
								query=""
								onSubmit={() => setIsSearchOpen(false)}
								onCancel={() => setIsSearchOpen(false)}
								isOpen={isSearchOpen}
							/>
						)}
					</div>

					{/* Second row: Chat Search Bar (positioned under new chat button on the right) */}
					{isChatSearchOpen && (
						<div
							className="mt-2 flex justify-end"
							style={{ WebkitAppRegion: 'no-drag' } as any}
						>
							<ChatSearchBar
								isOpen={isChatSearchOpen}
								onClose={() => setIsChatSearchOpen(false)}
								onSearch={(query) => {
									handleSearch(query);
								}}
								onNavigate={(index) => {
									navigateToMatch(index);
								}}
								totalMatches={totalMatches}
								currentMatchIndex={currentMatchIndex}
								onClear={clearSearch}
								roleFilter={roleFilter}
								onRoleFilterChange={handleRoleFilterChange}
							/>
						</div>
					)}
				</div>

				<MessageList
					chatContainerRef={chatContainerRef}
					chatHistory={chatHistory}
					isGenerating={isGenerating}
					handleFeedback={handleFeedback}
					selectedModel={selectedModel}
					onEditMessage={handleEditMessage}
					getHighlightData={getHighlightData}
				/>

				{/* Chat-area toasts rendered here */}
				{contextToast &&
					(() => {
						// Smart rendering logic for adaptive toasts
						if (contextToast.adaptToModal) {
							// Show in chat when modal is closed
							return !isModalOpen;
						}
						// Show traditional chat placement toasts
						return contextToast.placement === 'chat';
					})() && (
						<Toast
							isVisible={contextToast !== null}
							message={contextToast.message}
							type={contextToast.type}
							placement="chat"
							progress={contextToast.progress}
						/>
					)}

				{!isGenerating && (
					<PreviewModal
						files={selectedFiles}
						onRemoveFile={handleRemoveFile}
					/>
				)}

				{showUploadConfirmation && (
					<DragAndDropModal
						files={pendingFiles}
						folders={pendingFolders}
						isVLMSupported={modelSupportsVLM}
						onConfirm={handleConfirmUpload}
						onCancel={handleCancelUpload}
					/>
				)}

				<ChatInput
					ref={chatInputRef}
					isGenerating={isGenerating}
					selectedModel={selectedModel}
					selectedFiles={selectedFiles}
					attachedImages={attachedImages}
					setSelectedFiles={setSelectedFiles}
					setAttachedImages={setAttachedImages}
					handleStop={stopGeneration}
					fetchDocuments={fetchDocuments}
					isUploadingDocument={isUploadingDocument}
					setIsUploadingDocument={setIsUploadingDocument}
					inputs={inputs}
					setInputs={setInputs}
					setHistory={setHistory}
					chatId={chatId}
					selectedFilesDoc={selectedFilesDoc}
					setIsGenerating={setIsGenerating}
					isVLMSupported={modelSupportsVLM}
					onMagazineFiles={importMagazines}
				/>
			</div>

			{renameOpen && (
				<ChatRenameModal
					title={targetChatTitle || ''}
					isOpen={renameOpen}
					onSubmit={async (newTitle: string) => {
						if (targetChatId) {
							await renameChat(targetChatId, newTitle);
						}
						setRenameOpen(false);
						setTargetChatId(null);
						setTargetChatTitle(null);
					}}
					onCancel={() => {
						setRenameOpen(false);
						setTargetChatId(null);
						setTargetChatTitle(null);
					}}
				/>
			)}
		</>
	);
}
